import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { ApiInstance } from "utils/Api";
import { warnOnMismatchedContentType } from "utils/common";

type UploadFnPayload = {
  formData: FormData;
};

type UploadAttachmentProps = {
  isUploadingAttachment: boolean;
  attachmentUrl: string;
  handleUploadFileAttachment: ({ formData }: UploadFnPayload) => Promise<string>;
};

export const useUploadAttachment = (url: string, baseUrl: string, onUploadingChange?: (isUploading: boolean) => void): UploadAttachmentProps => {
  const { t } = useTranslation();

  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const activeUploads = useRef(0);

  const mutation = useMutation({
    mutationFn: async ({ formData }: UploadFnPayload) => (await ApiInstance.post(url, formData, baseUrl)).data.url,
    onSuccess: (url: string) => setAttachmentUrl(url),
    onError: e => toast.error(e.message)
  });

  const handleUploadFileAttachment = useCallback(
    async ({ formData }: UploadFnPayload): Promise<string> => {
      activeUploads.current += 1;
      if (activeUploads.current === 1) {
        setIsUploadingAttachment(true);
        onUploadingChange?.(true);
      }

      try {
        const uploadedUrl = await mutation.mutateAsync({ formData });
        const file = [...formData.values()].find(value => value instanceof File);
        if (file) await warnOnMismatchedContentType(file.name, uploadedUrl, t);

        return uploadedUrl;
      } finally {
        activeUploads.current -= 1;
        if (activeUploads.current === 0) {
          setIsUploadingAttachment(false);
          onUploadingChange?.(false);
        }
      }
    },
    [mutation.mutateAsync, onUploadingChange, t]
  );

  return { attachmentUrl, isUploadingAttachment, handleUploadFileAttachment };
};
